import { useCallback, useRef, useEffect } from 'react';
import {
  PROPERTIES_TO_FIELDS_MAP,
  COLUMN_IDS,
  HTML_TAG_NAMES,
  MAX_DEPTH,
} from '../pages/live-calculation-basket/utils/constants';
import { useFieldRefRegistry } from './useFieldRefRegistry';

/**
 * Finds the actual input element from a wrapper div using DOM element traversal (no querySelector).
 * @param {HTMLElement} wrapperElement - The wrapper div element
 * @returns {HTMLElement|null} - The actual input element or null if not found
 */
const findInputInWrapper = wrapperElement => {
  if (!wrapperElement || wrapperElement.tagName !== HTML_TAG_NAMES.DIV) {
    return null;
  }

  // Helper to recursively find input elements
  const findInputRecursive = (element, depth = 0) => {
    // Safety check: prevent stack overflow in extremely deep trees
    if (depth > MAX_DEPTH) {
      console.warn(
        `findInputRecursive: Maximum depth (${MAX_DEPTH}) reached. Stopping traversal to prevent stack overflow.`,
      );
      return null;
    }

    if (!element) return null;

    // Check if current element is an input, select, or textarea
    if (
      element.tagName === HTML_TAG_NAMES.INPUT ||
      element.tagName === HTML_TAG_NAMES.SELECT ||
      element.tagName === HTML_TAG_NAMES.TEXTAREA
    ) {
      return element;
    }

    // For react-select control
    if (element.classList && element.classList.contains('dropdown__control')) {
      return element;
    }

    // Check first child (increment depth)
    const firstChild = findInputRecursive(element.firstElementChild, depth + 1);
    if (firstChild) return firstChild;

    // Check next sibling (same depth level)
    const nextSibling = findInputRecursive(element.nextElementSibling, depth);
    if (nextSibling) return nextSibling;

    return null;
  };

  // Start search from wrapper's first child
  return findInputRecursive(wrapperElement.firstElementChild);
};

/**
 * Applies styles to an element and returns the original styles
 * @param {HTMLElement} element - The element to apply styles to
 * @param {Object} styles - Object containing style properties and values
 * @returns {Object} - Object containing the original styles that were replaced
 */
const applyStyles = (element, styles) => {
  if (!element || !element.style) {
    return {};
  }

  const originalStyles = {};

  Object.entries(styles).forEach(([property, value]) => {
    // Store original value
    originalStyles[property] = element.style[property];
    // Apply new value
    element.style[property] = value;
  });

  return originalStyles;
};

/**
 * Restores original styles to an element
 * @param {HTMLElement} element - The element to restore styles on
 * @param {Object} originalStyles - Object containing the original styles
 */
const restoreStyles = (element, originalStyles) => {
  if (!element || !element.style || !originalStyles) {
    return;
  }

  Object.entries(originalStyles).forEach(([property, value]) => {
    element.style[property] = value;
  });
};

/**
 * Highlights a table row with a blue border and background
 * @param {HTMLElement} row - The table row element to highlight
 * @param {number} duration - Duration in milliseconds to keep the highlight (default: 3000)
 */
const highlightTableRow = (row, duration = 3000) => {
  if (!row || row.tagName !== HTML_TAG_NAMES.TR) {
    return;
  }

  const highlightStyles = {
    background: 'rgba(0, 123, 255, 0.2)',
    border: '2px solid #007bff',
    boxShadow: '0 0 10px rgba(0, 123, 255, 0.5)',
    outline: '2px solid #007bff',
    transition: 'background 0.3s, border 0.3s, box-shadow 0.3s, outline 0.3s',
  };

  const originalStyles = applyStyles(row, highlightStyles);

  // Remove highlight after duration
  setTimeout(() => {
    restoreStyles(row, originalStyles);
  }, duration);
};

/**
 * Highlights an input field with a blue border and shadow
 * @param {HTMLElement} element - The input element to highlight
 * @param {number} duration - Duration in milliseconds to keep the highlight (default: 3000)
 */
const highlightInputField = (element, duration = 3000) => {
  if (!element || element.tagName === HTML_TAG_NAMES.TR || !element.style) {
    return;
  }

  const highlightStyles = {
    border: '2px solid #007bff',
    boxShadow: '0 0 0 3px rgba(0, 123, 255, 0.25)',
    outline: 'none',
    transition: 'border 0.3s, box-shadow 0.3s',
  };

  const originalStyles = applyStyles(element, highlightStyles);

  // Remove highlight after duration
  setTimeout(() => {
    restoreStyles(element, originalStyles);
  }, duration);
};

/**
 * Options for focus + highlight behavior when using the hook.
 * @typedef {Object} FocusOptions
 * @property {number} [scrollDelay=500] - Delay before focus+highlight after scroll (ms)
 * @property {ScrollBehavior} [scrollBehavior='smooth'] - 'smooth' | 'auto' | 'instant'
 * @property {number} [highlightDuration=3000] - How long the highlight stays (ms)
 */

/**
 * React hook to focus & highlight a field by its *property* name.
 *
 * Uses React refs to scroll, focus, and highlight fields.
 * `fieldName` is the logical id used in PROPERTIES_TO_FIELDS_MAP (e.g. 'name', 'description', COLUMN_IDS.SHARES).
 * Fields must be registered with registerFieldRef for this to work.
 */

export const useFocusFieldByProperty = () => {
  const { registerFieldRef, getFieldRef } = useFieldRefRegistry();

  // Track the single timeout for cleanup
  const timeoutRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const focusFieldByProperty = useCallback(
    (property, options = {}) => {
      const { scrollDelay = 500, scrollBehavior = 'smooth', highlightDuration = 3000 } = options;

      // Clear previous timeout if any
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const fieldName = PROPERTIES_TO_FIELDS_MAP[property];

      if (!fieldName) {
        console.warn(`Could not map property to field name: ${property}`);
        return null;
      }

      // If this is a metadata table field (shares or coverage), first scroll the table container into view
      if (fieldName === COLUMN_IDS.SHARES || fieldName === COLUMN_IDS.COVERAGE) {
        const tableContainer = getFieldRef('metadata_table_container');
        if (tableContainer) {
          tableContainer.scrollIntoView({
            behavior: scrollBehavior,
            block: 'start',
            inline: 'nearest',
          });
        }
      }

      // Get element from React ref registry
      const elementFromRef = getFieldRef(fieldName);

      if (!elementFromRef) {
        console.warn(
          `No ref registered for field "${fieldName}" (property: "${property}"). ` +
            `Make sure to register the field with registerFieldRef('${fieldName}').`,
        );
        // For metadata table fields, at least we scrolled to the table, so return the table container
        if (fieldName === COLUMN_IDS.SHARES || fieldName === COLUMN_IDS.COVERAGE) {
          const tableContainer = getFieldRef('metadata_table_container');
          return tableContainer;
        }
        return null;
      }

      // If it's a wrapper div, find the actual input element using DOM traversal
      let finalElement = elementFromRef;
      if (elementFromRef.tagName === HTML_TAG_NAMES.DIV) {
        const foundInput = findInputInWrapper(elementFromRef);
        if (foundInput) {
          finalElement = foundInput;
        }
      }

      // Scroll to element using ref
      elementFromRef.scrollIntoView({
        behavior: scrollBehavior,
        block: 'center',
        inline: 'nearest',
      });

      // Single timeout for focus + highlight
      timeoutRef.current = setTimeout(() => {
        try {
          // Focus the element
          if (finalElement && finalElement.focus) {
            finalElement.focus();
          }

          // Highlight input
          highlightInputField(finalElement, highlightDuration);

          // If this is a table field, highlight its row as well
          if (fieldName === COLUMN_IDS.SHARES || fieldName === COLUMN_IDS.COVERAGE) {
            const row = finalElement.closest('tr');
            if (row) {
              highlightTableRow(row, highlightDuration);
            }
          }
        } catch (e) {
          console.warn('Could not focus/highlight element from ref:', e);
        }
        timeoutRef.current = null;
      }, scrollDelay);

      return finalElement;
    },
    [getFieldRef],
  );

  return {
    registerFieldRef,
    focusFieldByProperty,
  };
};
